import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import styled, { css, useTheme } from "styled-components/macro";
import { NumberParam, StringParam, useQueryParams } from "use-query-params";
import {
  ButtonWithConfirmDialog,
  DestructiveButtonMediumV2,
  LinkButtonLargeV2,
  PrimaryButtonMediumV2,
  PrimaryButtonSmallV2,
  SecondaryButtonSmallV2,
} from "../../../components/Buttons/Buttons";
import { PageHeader, PageTitle } from "../../../layout/portalPageLayout";

import type { AxiosError, AxiosResponse } from "axios";
import axios from "axios";
import type { DataMutate, WithPagination } from "../../../types/types";

import { useTranslation } from "react-i18next";
import ReactTooltip from "react-tooltip";
import useSWR from "swr";
import { useAuthContext } from "../../../components/Auth";
import { CompletionScore } from "../../../components/CompletionScore/CompletionScore";
import { DelayedSpinner } from "../../../components/DelayedSpinner/DelayedSpinner";
import { ErrorPlaceholder } from "../../../components/Error";
import {
  HeaderContainer,
  SubheaderContainer,
  SubheaderInformationContainer,
} from "../../../components/Layout/Layout";
import { LinkUnstyled } from "../../../components/LinkUnstyled/LinkUnstyled";
import { Modal } from "../../../components/Modal/Modal";
import { Notifications } from "../../../components/Notifications/NotificationsContext";
import { ConfirmDialog } from "../../../components/ConfirmDialog/ConfirmDialog";
import { ObjectReferenceChangeConfirmDialog } from "../../../components/ObjectReference/ObjectReferenceChangeConfirmDialog";
import {
  checkObjectReferenceImpact,
  getProductArchiveWarningMessage,
  requestObjectReferenceActionConfirmation,
} from "../../../components/ObjectReference/utils";
import {
  getProductStatusColor,
  getProductStatusText,
  StatusBadge,
  StatusLeft,
} from "../../../components/Status/Status";
import {
  InnerTitleText,
  SubheaderContentText,
} from "../../../components/Typography/Typography";
import { endpoints } from "../../../endpoints";
import type { StorefrontMetaData } from "../../../types/types";
import type {
  Assets,
  PIMProduct,
  ProductListPriceSchema,
  ProductStatusType,
  TdsGeneratedAssetSchema,
  PIMProductStatusAck,
} from "../../../types/types.PIM";
import { useRoutePath } from "../../../util/Routing";
import {
  makeUrlWithParams,
  useMediaQueries,
  useStoreState,
} from "../../../util/util";
import { useLogProfileView } from "../../../util/activityTracking";
import type { IProductVisibilityData } from "./Connections/Connections";
import { ProductShareButton } from "./ProductShareButton";
import { ProductTabs } from "./util";
import moment from "moment";
import {
  CalendarIcon,
  CardCompletionBadgeIcon,
  CaretLeft,
  CartOutlineIcon,
  LabFlaskIcon,
  MasterProductIcon,
  TeamsIcon,
  TemplatesNavIcon,
} from "../../../components/Icons/Icons";
import {
  Breadcrumb,
  FullwidthPageWrapper,
  ProductSidebar,
  ProductSidebarContent,
  ProductSidebarWrapper,
} from "../../../components/ProductsLayout/SharedProductsLayout";
import { ProfileDetailContentContainer } from "../../../layout/shared/ProfileDetailLayout";
import { screenSize } from "../../../theme";
import { ProductGroupCard } from "./ProductOverview/ProductGroupCard";
import {
  ImageWrapper,
  MasterVariantBadge,
  PlaceholderImage,
  PlaceholderLogo,
} from "../../../components/CardProduct/CardProduct";
import { SellerCreateSampleRequest } from "../../Seller/SellerCreateSampleRequest/SellerCreateSampleRequest";
import { SellerCreateQuote } from "../../Seller/SellerCreateQuote/SellerCreateQuote";
import { RequestApprovalButton } from "./RequestApproval/RequestApprovalButton";
import { useApprovalWorkflowEnabled } from "./Approvals/hooks/useApprovalWorkflowEnabled";
import { useApprovals } from "./Approvals/hooks/useApprovals";
import { ApprovalEligibilityProvider } from "./Approvals/ApprovalEligibilityContext";
import { fontSizeTokens, spacingTokens } from "../../../designTokens";
import { useDocumentAvailability } from "./hooks/useDocumentAvailability";
import { productHasGeneratedAssetInstances } from "./hooks/useProductDocumentAvailability";
import {
  getActiveTemplateGeneratedAssets,
  useTemplateGeneratedAssets,
} from "./hooks/useTemplateGeneratedAssets";
import { useRelatedEntities } from "../../../components/ObjectReference/RelatedEntitiesTab";
import { getProductTypePermissionSuffix } from "./ProductOverview/product_overview.utils";

export const ProductHeaderContainer = styled.div<{
  pageLayout: string;
  isXlScreen: boolean;
  productBackground: string;
}>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  top: 0;
  z-index: 3;
  background-color: ${({ theme }) => theme.resolve("color_neutral_white")};
  padding: ${({ theme }) => theme.resolve("spacing_xl")};
  padding-bottom: 0;
  ${({ pageLayout, isXlScreen, productBackground }) =>
    pageLayout === "two_column" &&
    isXlScreen &&
    css`
      background-color: ${productBackground};
    `}
  @media (${screenSize.small}) {
    position: relative;
  }
`;

export const ProductHeader = styled(PageHeader)`
  align-items: flex-start;
  justify-content: flex-start;
  margin-top: 10px;
  margin-bottom: ${({ theme }) => theme.resolve("spacing_md")};
  width: 100%;
`;

const ProductHeaderImage = styled.img`
  min-width: 100px;
  max-width: 200px;
  max-height: 150px;
  margin-right: 15px;
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.primaryBorder};
  border-radius: ${({ theme }) => theme.resolve("radius_md")};
  padding: 1px;
  align-self: center;
  cursor: pointer;
`;

export const ProductHeaderActions = styled.div`
  justify-self: end;
  display: flex;
  min-width: 100px;
  gap: ${({ theme }) => theme.resolve("spacing_xl")};
  align-items: center;
  button {
    margin-bottom: 0;
  }
  svg {
    width: 20px !important;
    height: 20px !important;
  }
`;
const ScoreLabel = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.secondaryTextColor};
`;
const ModalWrapper = styled.div`
  padding: ${({ theme }) => theme.resolve("spacing_2xl")};
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const PaddedPrimaryButton = styled(PrimaryButtonMediumV2)`
  padding: ${({ theme }) => theme.resolve("spacing_md")}
    ${({ theme }) => theme.resolve("spacing_xl")};
  white-space: nowrap;
  svg {
    vertical-align: bottom;
  }
  margin-bottom: 5px;
`;

const PaddedDestructiveButton = styled(DestructiveButtonMediumV2)`
  padding: ${({ theme }) => theme.resolve("spacing_md")}
    ${({ theme }) => theme.resolve("spacing_xl")};
  white-space: nowrap;
  svg {
    vertical-align: bottom;
  }
  margin-bottom: 5px;
`;

export type ProductMutate = DataMutate<PIMProduct>;

/**
 * Product detail page. Used for seller admin.
 */
export const ProductDetailPage = () => {
  const { tenant_id, storefront_id, storeDispatch, slug, storefront_metadata } =
    useStoreState();
  const pageLayout =
    storefront_metadata.theme_object.designTokens?.productDetail
      ?.product_detail_layout?.value || "single_column";
  const pageContainerSettings =
    storefront_metadata.theme_object.designTokens?.productDetail
      ?.container_settings?.value || "no_containers";

  const { hasPermission, roleIsSomeKindOfSeller, isLoadingSession } =
    useAuthContext();

  const hasEditOrPublishPermission =
    hasPermission("modify_products") || hasPermission("publish_products");

  const { search, pathname } = useLocation();

  const { product_id: productId } = useParams<{ product_id: string }>();

  const { adminPath } = useRoutePath();

  const { t } = useTranslation();

  const { notifyError, notifySuccess } = useContext(Notifications);

  const history = useHistory();
  const theme = useTheme();
  const [loadingState, setLoadingState] = useState<ProductStatusType>();
  const [showArchiveWarningDialog, setShowArchiveWarningDialog] =
    useState(false);
  const [showArchiveBasicConfirmDialog, setShowArchiveBasicConfirmDialog] =
    useState(false);
  const [pendingArchiveAction, setPendingArchiveAction] = useState(false);
  const [showProductImageModal, setShowProductImageModal] = useState(false);
  const [fullSizeProductImage, setFullSizeProductImage] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [teamName, setTeamName] = useState<string>("");
  const {
    isEnabled: isApprovalWorkflowEnabled,
    isLoading: isApprovalWorkflowLoading,
  } = useApprovalWorkflowEnabled();
  const [in_progress_approvals_count, setInProgressApprovalsCount] =
    useState<number | null>(null);
  const [visibilityData, setVisibilityData] =
    useState<IProductVisibilityData>();
  // URL query parameters for tab management
  const [query, setQuery] = useQueryParams({
    tab: NumberParam,
    tabKey: StringParam,
    documentType: StringParam,
    changeRequestId: StringParam,
  });
  const { data: metadata } = useSWR<StorefrontMetaData>(
    endpoints.v1_storefronts_id_metadata(storefront_id)
  );
  // We cant annotate the type inline while destucturing but "mutate"
  // here is type ProductMutate
  const {
    data: product,
    error: productError,
    mutate,
  } = useSWR<PIMProduct, AxiosError>(
    productId && !isLoadingSession
      ? makeUrlWithParams(
          endpoints.v2_tenants_id_pim_products_id(tenant_id, productId),
          {
            use_database: "true",
            return_staged: hasEditOrPublishPermission ? "true" : "false",
          }
        )
      : null,
    { revalidateOnFocus: false }
  );
  // AP-1166: one profile view per product opened. A staged draft's ID is fine:
  // the backend resolves the shared product number.
  useLogProfileView(
    product?.id ? { type: "product", id: product.id } : undefined
  );
  const approvalsProductKey = product?.product_number || product?.id;
  const shouldSeedApprovalsCount =
    isApprovalWorkflowEnabled &&
    in_progress_approvals_count === null &&
    !!approvalsProductKey;
  const {
    in_progress_approvals_count: approvalsCountFromSummary,
    isLoading: isApprovalsCountSeeding,
  } = useApprovals({
    productId: approvalsProductKey,
    limit: 1,
    offset: 0,
    enabled: shouldSeedApprovalsCount,
  });
  useEffect(() => {
    setInProgressApprovalsCount(null);
  }, [approvalsProductKey]);
  useEffect(() => {
    if (!isApprovalWorkflowEnabled) {
      setInProgressApprovalsCount(null);
      return;
    }
    if (
      in_progress_approvals_count === null &&
      !isApprovalsCountSeeding &&
      approvalsProductKey
    ) {
      setInProgressApprovalsCount(approvalsCountFromSummary);
    }
  }, [
    approvalsCountFromSummary,
    approvalsProductKey,
    in_progress_approvals_count,
    isApprovalWorkflowEnabled,
    isApprovalsCountSeeding,
  ]);
  const displayCount = in_progress_approvals_count ?? 0;

  // Fetch list prices to determine if the pricing tab should be shown
  const { data: listPricesData, error: listPricesError } = useSWR<
    WithPagination<{ data: ProductListPriceSchema[] }>
  >(
    product
      ? makeUrlWithParams(
          endpoints.v2_tenants_tenant_id_products_product_id_list_prices(
            tenant_id,
            product.id
          ),
          { limit: 1, offset: 0 }
        )
      : null
  );

  const show_pricing_tab = (listPricesData?.pagination?.total ?? 0) > 0;
  const isListPricesLoading = Boolean(
    product && !listPricesData && !listPricesError
  );

  const { hasAssetsOrExternalLinks, isLoading: isDocumentAvailabilityLoading } =
    useDocumentAvailability("product", product?.id);

  const targetObjectIdForRelatedEntities = product
    ? ["staged", "unpublished_staged"].includes(product.display_status)
      ? product.published_product_id ?? product.id
      : product.id
    : undefined;

  const { data: relatedEntities, error: relatedEntitiesError } =
    useRelatedEntities(
      tenant_id,
      "product",
      targetObjectIdForRelatedEntities,
      storefront_metadata.object_reference_enabled
    );

  const isRelatedEntitiesLoading = Boolean(
    storefront_metadata.object_reference_enabled &&
      targetObjectIdForRelatedEntities &&
      relatedEntities === undefined &&
      !relatedEntitiesError
  );

  const { data: templateGeneratedAssets } = useTemplateGeneratedAssets(
    product?.product_schema?.template_id
  );
  const activeTemplateGeneratedAssets = getActiveTemplateGeneratedAssets(
    templateGeneratedAssets
  );

  const { isSmallScreen, isXlScreen, isLargeScreen } = useMediaQueries();
  const [headerHeight, setHeaderHeight] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  // Modal state for quote/sample requests
  const [quoteVariant, setQuoteVariant] = useState<"seller" | null>(null);
  const [sampleVariant, setSampleVariant] = useState<"seller" | null>(null);

  // Determine storefront configuration for quotes and samples
  const quotingEnabled =
    storefront_metadata.quote_requests_visibility === "enabled";
  const samplingEnabled =
    storefront_metadata.sample_requests_visibility === "enabled";

  const showCreateQuote =
    roleIsSomeKindOfSeller &&
    (product?.status === "published" || product?.status === "staged") &&
    quotingEnabled &&
    product?.pim_product_type !== "master";
  const showCreateSample =
    roleIsSomeKindOfSeller &&
    samplingEnabled &&
    (product?.status === "published" || product?.status === "staged") &&
    product?.pim_product_type !== "master";

  const handleTabChange = async (index: number) => {
    setCurrentTab(index);
    // Convert to 1-based index for URL (tabs are 1-indexed in URLs)
    // Clear documentType / tabKey when user manually changes tabs
    setQuery({
      tab: index + 1,
      documentType: undefined,
      tabKey: undefined,
      changeRequestId: undefined,
    });
  };

  const handleDocumentTypeTabChange = useCallback(
    (index: number) => {
      setCurrentTab(index);
      setQuery({
        tab: index + 1,
        documentType: query.documentType,
        changeRequestId: undefined,
      });
    },
    [query.documentType, setQuery]
  );

  // Programmatic tab selection (deep links, tab-list remaps). Must not go
  // through handleTabChange, which clears tabKey / changeRequestId.
  const handleRequestedTabChange = useCallback((index: number) => {
    setCurrentTab(index);
  }, []);

  const goBackToProductsList = () => {
    history.push(
      `${adminPath}/pim/products${
        sessionStorage.getItem("products_search") ?? ""
      }`
    );
  };

  useEffect(() => {
    if (product && ref?.current) {
      setHeaderHeight(ref.current.clientHeight);
    }
  }, [product, ref]);

  useEffect(() => {
    return () => {
      setTimeout(() => {
        if (!window.location.pathname.includes("/pim/products")) {
          sessionStorage.removeItem("selectedRows");
        }
      }, 0);
    };
  }, [pathname]);

  useEffect(() => {
    function handleResize() {
      if (product && ref?.current) {
        setHeaderHeight(ref.current.clientHeight);
      }
    }

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  });

  useEffect(() => {
    const teamNamesString = (() => {
      const teams = product?.team_names;
      const length = teams?.length;

      if (!length) {
        return t("None");
      } else if (length <= 2) {
        return teams.join(", ");
      } else {
        const firstTwo = teams.slice(0, 2);
        return `${firstTwo.join(", ")} + ${length - 2} ${t("more")}`;
      }
    })();
    setTeamName(teamNamesString);
  }, [product?.team_names, t]);

  // Numeric ?tab= is only used when the URL is not asking for a tab by key.
  // ?changeRequestId= / ?tabKey= must win, otherwise a stale tab index from a
  // previous session (or a still-loading tab list) steals the deep link.
  useEffect(() => {
    if (query.changeRequestId || !query.tab) {
      return;
    }
    const tabIndex = query.tab - 1;
    if (tabIndex >= 0 && tabIndex !== currentTab) {
      setCurrentTab(tabIndex);
    }
  }, [query.tab, query.tabKey, query.changeRequestId, currentTab]);

  if (productError) {
    const is404or403 =
      productError.response?.status === 404 ||
      productError.response?.status === 403;
    const errorMessage = is404or403
      ? t(
          "This product is not available or you don't have permission to view it."
        )
      : t("There was an error loading the product.");

    return (
      <>
        <LinkButtonLargeV2 onClick={() => goBackToProductsList()}>
          <CaretLeft />
          {t("Products")}
        </LinkButtonLargeV2>
        <ErrorPlaceholder message={errorMessage} />
      </>
    );
  }
  if (!product) {
    // Product data is still loading.
    return <DelayedSpinner />;
  }

  // const productModifiedAt = formatDateTime(
  //   product.last_modified_full?.modified_at
  // );
  // const productModifiedBy = product.last_modified_full?.modified_by;

  const replaceProductId = async (productId: string) => {
    history.replace(
      search.includes("source=new")
        ? `${adminPath}/pim/products/${productId}?source=new`
        : `${adminPath}/pim/products/${productId}`
    );
    await mutate();
  };

  const productCoverImage = product?.cover_image?.signed_url;

  const pollUntilFinished = async (
    url: string,
    /**
     * Polling interval in milliseconds
     */
    interval: number = 2000,
    /**
     * Maximum time to wait before giving up in milliseconds
     */
    timeout: number = 30000
  ): Promise<AxiosResponse<Assets>> => {
    const startTime = Date.now();

    const checkStatus = async (): Promise<AxiosResponse<Assets>> => {
      const response = await axios.get(url);
      let timeout_id: NodeJS.Timeout | undefined = undefined;

      if (
        response.data.status === "finished" ||
        Date.now() - startTime > timeout
      ) {
        clearTimeout(timeout_id);
        return response;
      } else {
        await new Promise((resolve) => {
          timeout_id = setTimeout(resolve, interval);
        });
        return checkStatus();
      }
    };

    return checkStatus();
  };
  /**
   * The first series of calls is to kick of an async job to regenerate the
   * assets, the second array of calls is to poll the status of the async job.
   */
  const regenerateAssets = async (
    generatedAssets: TdsGeneratedAssetSchema[],
    productId: string
  ) => {
    const genAssetsPromiseArray = generatedAssets.reduce<
      Promise<AxiosResponse<Assets>>[]
    >((acc, genAsset) => {
      if (genAsset.is_active) {
        acc.push(
          axios.post(
            endpoints.v2_tenants_tenant_id_products_product_id_generate_assets_id_async(
              tenant_id,
              productId,
              genAsset.id
            ),
            null,
            {
              params: {
                return_invisible_asset: false,
                regenerate_product_asset: true,
              },
            }
          )
        );
      }
      return acc;
    }, []);

    const genAssetsToPollPromiseArray = generatedAssets.reduce<
      Promise<AxiosResponse<Assets>>[]
    >((acc, genAsset) => {
      if (genAsset.is_active) {
        acc.push(
          pollUntilFinished(
            endpoints.v2_tenants_tenant_id_products_product_id_generate_assets_id_status(
              tenant_id,
              productId,
              genAsset.id
            )
          )
        );
      }
      return acc;
    }, []);

    await Promise.all(genAssetsPromiseArray);
    await Promise.all(genAssetsToPollPromiseArray);
  };

  const canRegenerateAssets = async (productId: string) =>
    activeTemplateGeneratedAssets.length > 0 &&
    (await productHasGeneratedAssetInstances(tenant_id, productId));

  type HandleStatusUpdateOptions = {
    skipArchiveConfirmation?: boolean;
  };

  const handleStatusUpdate = async (
    statusToBecome: ProductStatusType,
    options?: HandleStatusUpdateOptions
  ): Promise<boolean> => {
    setLoadingState(statusToBecome);

    try {
      if (statusToBecome === "published") {
        storeDispatch({ type: "SET_IS_PUBLISHING_PRODUCT", payload: true });
      }

      if (statusToBecome === "archived" && !options?.skipArchiveConfirmation) {
        const needsConfirmation =
          await requestObjectReferenceActionConfirmation({
            action: "archived",
            objectReferenceEnabled: Boolean(
              storefront_metadata.object_reference_enabled
            ),
            shouldCheckImpact: true,
            checkImpact: () =>
              checkObjectReferenceImpact({
                tenantIdOrSlug: tenant_id,
                objectType: "product",
                objectId: product.id,
              }),
            setPendingAction: () => setPendingArchiveAction(true),
            showWarningDialog: () => setShowArchiveWarningDialog(true),
            showBasicConfirmDialog: () =>
              setShowArchiveBasicConfirmDialog(true),
          });
        if (needsConfirmation) {
          return false;
        }
      }

      await axios.post<
        { status: ProductStatusType },
        AxiosResponse<PIMProductStatusAck>
      >(
        endpoints.v2_storefronts_id_or_slug_products_id_or_slug_status(
          storefront_id,
          product.product_number ?? product.id
        ),
        { status: statusToBecome }
      );
      const refreshedProduct = await mutate();
      if (
        (statusToBecome === "published" || statusToBecome === "discarded") &&
        refreshedProduct
      ) {
        try {
          if (
            statusToBecome === "published" &&
            (await canRegenerateAssets(refreshedProduct.id))
          ) {
            await regenerateAssets(
              activeTemplateGeneratedAssets,
              refreshedProduct.id
            );
            notifySuccess(t("Assets regenerated for product"));
          }
        } catch (error) {
          console.error(error);
          notifyError(t("There was an error regenerating assets"));
        }
        const product_id =
          refreshedProduct.product_number ??
          refreshedProduct.published_product_id;
        await replaceProductId(product_id);
      }
      setLoadingState(undefined);
      notifySuccess(t("Product status updated successfully"));
      return true;
    } catch (error) {
      const errorMessage = (error as AxiosError)?.response?.data?.message;
      notifyError(
        errorMessage
          ? errorMessage
          : t("There was an error updating the product status"),
        {
          error,
        }
      );
      setLoadingState(undefined);
      return false;
    } finally {
      storeDispatch({ type: "SET_IS_PUBLISHING_PRODUCT", payload: false });
    }
  };

  const discardConfirmMessage = t(
    "You will lose all changes made to this product. Are you sure you want to continue?"
  );
  const productTypePermissionSuffix = getProductTypePermissionSuffix(
    product.pim_product_type
  );
  const isDisabledBasedOnStatusPermission = (status: ProductStatusType) => {
    if (
      status === "published" &&
      product.mandatory_completion_score !== null &&
      product.product_schema.show_mandatory_completion_score &&
      product.mandatory_completion_score <
        (metadata?.publish_completion_score_threshold ?? 0)
    ) {
      return true;
    }

    if (
      status === "published" &&
      (!hasPermission("publish_products") ||
        !hasPermission(`publish_${productTypePermissionSuffix}`) ||
        !product.is_publishable)
    ) {
      return true;
    }

    if (
      status === "unpublished" &&
      (!hasPermission("unpublish_products") ||
        !hasPermission(`unpublish_${productTypePermissionSuffix}`) ||
        !product.is_publishable)
    ) {
      return true;
    }

    if (
      ["published", "staged", "unpublished"].includes(status) &&
      (!hasPermission("modify_products") ||
        !hasPermission(`modify_${productTypePermissionSuffix}`))
    ) {
      return true;
    }

    if (["discarded", "archived"].includes(status)) {
      if (!hasPermission("delete_products")) {
        return true;
      }
      if (!hasPermission(`delete_${productTypePermissionSuffix}`)) {
        return true;
      }
    }

    return false;
  };

  const updateLoadingAndDisabledState = (status: ProductStatusType) => {
    if (!product.is_editable) {
      return {
        loading: loadingState === status,
        disabled: true,
      };
    } else {
      const disabledStatus = isDisabledBasedOnStatusPermission(status)
        ? true
        : Boolean(loadingState);
      return {
        loading: loadingState === status,
        disabled: disabledStatus,
      };
    }
  };

  type StatusButtonAction =
    | "publish"
    | "unpublish"
    | "discard"
    | "archive"
    | "restore";

  const actionButtonDisabledMessage = (action: StatusButtonAction): string => {
    if (!product.is_editable) {
      return product.is_locked && hasPermission("modify_products")
        ? t("This product is locked for edit and cannot be edited")
        : t("You don't have permission to modify this product");
    }

    switch (action) {
      case "publish":
        if (
          !hasPermission("publish_products") &&
          product.status !== "published"
        ) {
          return t("You do not have permission to publish products");
        }
        break;
      case "unpublish":
        if (
          !hasPermission("unpublish_products") &&
          product.status === "published"
        ) {
          return t("You do not have permission to unpublish products");
        }
        break;
      case "restore":
        if (
          !hasPermission("publish_products") &&
          product.status === "archived"
        ) {
          return t("You do not have permission to restore products");
        }
        break;
      case "discard":
        if (
          !hasPermission("publish_products") &&
          product.status !== "discarded"
        ) {
          return t("You do not have permission to discard products");
        }
        break;
      case "archive":
        if (
          !hasPermission("unpublish_products") &&
          product.status !== "archived"
        ) {
          return t("You do not have permission to archive products");
        }
        break;
    }

    return "";
  };

  const publishButtonDisabledMessage = (() => {
    if (!product.is_editable) {
      return product.is_locked &&
        hasPermission("modify_products") &&
        hasPermission(`modify_${productTypePermissionSuffix}`)
        ? t("This product is locked for edit and cannot be edited")
        : t("You don't have permission to modify this product");
    }

    if (
      product.mandatory_completion_score !== null &&
      product.product_schema.show_mandatory_completion_score &&
      product.mandatory_completion_score <
        (metadata?.publish_completion_score_threshold ?? 0)
    ) {
      return t(
        "Mandatory Completion Score must be at least {{threshold}}% to publish",
        {
          threshold: metadata?.publish_completion_score_threshold,
        }
      );
    }

    if (!hasPermission("publish_products")) {
      return t("You do not have permission to publish products");
    }

    return "";
  })();

  const statusActionButtons = () => {
    switch (product.status) {
      case "draft":
        return (
          <>
            <ButtonWithConfirmDialog
              Button={PaddedDestructiveButton}
              testid="draft_discard"
              confirmMessage={discardConfirmMessage}
              handleConfirm={async () => {
                const wasDeleted = await handleStatusUpdate("discarded");
                if (wasDeleted) {
                  goBackToProductsList();
                }
              }}
              loading={updateLoadingAndDisabledState("discarded").loading}
              disabled={updateLoadingAndDisabledState("discarded").disabled}
              buttonText={t("Delete")}
              datatip={actionButtonDisabledMessage("discard")}
              datafor="draft_tooltip"
            />
            <ReactTooltip id="draft_tooltip" />
            {isApprovalWorkflowEnabled && (
              <RequestApprovalButton
                product={product}
                productId={product.product_number || product.id}
                onApprovalRequested={() => mutate()}
              />
            )}
            <div
              data-tip={publishButtonDisabledMessage}
              data-for="publish_tooltip"
            >
              <PaddedPrimaryButton
                onClick={() => handleStatusUpdate("published")}
                loading={updateLoadingAndDisabledState("published").loading}
                disabled={updateLoadingAndDisabledState("published").disabled}
              >
                {t("Publish")}
              </PaddedPrimaryButton>
            </div>
            <ReactTooltip id="publish_tooltip" effect="solid" />
          </>
        );
      case "staged":
        return (
          <>
            <ButtonWithConfirmDialog
              Button={PaddedDestructiveButton}
              testid="staged_discard"
              confirmMessage={discardConfirmMessage}
              handleConfirm={() => handleStatusUpdate("discarded")}
              buttonText={t("Discard Changes")}
              loading={updateLoadingAndDisabledState("discarded").loading}
              disabled={updateLoadingAndDisabledState("discarded").disabled}
              datatip={actionButtonDisabledMessage("discard")}
              datafor="staged_tooltip"
            />
            <ReactTooltip id="staged_discard" />
            {isApprovalWorkflowEnabled && (
              <RequestApprovalButton
                product={product}
                productId={product.product_number || product.id}
                onApprovalRequested={() => mutate()}
              />
            )}
            <div
              data-tip={publishButtonDisabledMessage}
              data-for="publish_tooltip"
            >
              <PaddedPrimaryButton
                onClick={() => handleStatusUpdate("published")}
                loading={updateLoadingAndDisabledState("published").loading}
                disabled={updateLoadingAndDisabledState("published").disabled}
              >
                {t("Publish")}
              </PaddedPrimaryButton>
            </div>
            <ReactTooltip id="publish_tooltip" effect="solid" />
          </>
        );
      case "published":
        return (
          <>
            {isApprovalWorkflowEnabled && (
              <RequestApprovalButton
                product={product}
                productId={product.product_number || product.id}
                onApprovalRequested={() => mutate()}
              />
            )}
            <div
              data-tip={actionButtonDisabledMessage("unpublish")}
              data-for="action_tooltip"
            >
              <PaddedPrimaryButton
                onClick={() => handleStatusUpdate("unpublished")}
                loading={updateLoadingAndDisabledState("unpublished").loading}
                disabled={updateLoadingAndDisabledState("unpublished").disabled}
                style={{ marginBottom: spacingTokens.spacing_none }}
              >
                {t("Unpublish")}
              </PaddedPrimaryButton>
            </div>
            <ReactTooltip id="action_tooltip" effect="solid" />
          </>
        );
      case "unpublished":
        return (
          <>
            {isApprovalWorkflowEnabled && (
              <RequestApprovalButton
                product={product}
                productId={product.product_number || product.id}
                onApprovalRequested={() => mutate()}
              />
            )}
            <PaddedDestructiveButton
              onClick={() => handleStatusUpdate("archived")}
              loading={updateLoadingAndDisabledState("archived").loading}
              disabled={updateLoadingAndDisabledState("archived").disabled}
              data-tip={actionButtonDisabledMessage("archive")}
              data-for="unpublished_tooltip"
            >
              {t("Archive")}
            </PaddedDestructiveButton>
            <div
              data-tip={publishButtonDisabledMessage}
              data-for="publish_tooltip"
            >
              <PaddedPrimaryButton
                onClick={() => handleStatusUpdate("published")}
                loading={updateLoadingAndDisabledState("published").loading}
                disabled={updateLoadingAndDisabledState("published").disabled}
                style={{ marginBottom: spacingTokens.spacing_none }}
              >
                {t("Publish")}
              </PaddedPrimaryButton>
            </div>
            <ReactTooltip id="publish_tooltip" effect="solid" />
          </>
        );
      case "unpublished_staged":
        return (
          <>
            <ButtonWithConfirmDialog
              Button={PaddedDestructiveButton}
              testid="unpublished_staged_discard"
              confirmMessage={discardConfirmMessage}
              handleConfirm={() => handleStatusUpdate("discarded")}
              buttonText={t("Discard Changes")}
              loading={updateLoadingAndDisabledState("discarded").loading}
              disabled={updateLoadingAndDisabledState("discarded").disabled}
              datatip={actionButtonDisabledMessage("discard")}
              datafor="unpublished_staged_tooltip"
            />
            {isApprovalWorkflowEnabled && (
              <RequestApprovalButton
                product={product}
                productId={product.product_number || product.id}
                onApprovalRequested={() => mutate()}
              />
            )}
            <div
              data-tip={publishButtonDisabledMessage}
              data-for="publish_tooltip"
            >
              <PaddedPrimaryButton
                onClick={() => handleStatusUpdate("published")}
                loading={updateLoadingAndDisabledState("published").loading}
                disabled={updateLoadingAndDisabledState("published").disabled}
              >
                {t("Publish")}
              </PaddedPrimaryButton>
            </div>
            <ReactTooltip id="publish_tooltip" effect="solid" />
          </>
        );
      case "archived":
        return (
          <>
            <div
              data-tip={actionButtonDisabledMessage("restore")}
              data-for="action_tooltip"
            >
              <PaddedPrimaryButton
                onClick={() => handleStatusUpdate("unpublished")}
                loading={updateLoadingAndDisabledState("unpublished").loading}
                disabled={updateLoadingAndDisabledState("unpublished").disabled}
              >
                {t("Restore")}
              </PaddedPrimaryButton>
            </div>
            <ReactTooltip id="action_tooltip" effect="solid" />
          </>
        );
      default:
        return <></>;
    }
  };

  const isLinkDisabled: boolean = product.status !== "published";

  const handleVisibilityDataFetched = (
    visibilityData: IProductVisibilityData
  ) => {
    setVisibilityData(visibilityData);
  };

  const isConnectionOn = visibilityData?.visible_storefronts.some(
    ({ slug: storefrontSlug }) => storefrontSlug === slug
  );

  const isTabDisabled: boolean = loadingState === "published";

  const pageContent = (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        height: "100%",
      }}
    >
      {pageLayout === "two_column" && isXlScreen && (
        <ProductSidebarWrapper>
          <ProductSidebar>
            <ProductSidebarContent>
              <ProductHeader>
                <ImageWrapper>
                  {productCoverImage ? (
                    <img
                      src={productCoverImage}
                      alt={product.name || "Product"}
                      onClick={() => setShowProductImageModal(true)}
                    />
                  ) : (
                    <PlaceholderImage>
                      <PlaceholderLogo
                        src={theme.portalLogoUrl}
                        alt="Company Logo"
                      />
                    </PlaceholderImage>
                  )}
                  <MasterVariantBadge
                    pim_product_type={product.pim_product_type ?? "standard"}
                  />
                </ImageWrapper>
                <HeaderContainer>
                  <PageTitle style={{ fontSize: "var(--font_size_2xl)" }}>
                    {product.name}
                  </PageTitle>

                  <SubheaderInformationContainer>
                    <SubheaderContainer>
                      {isApprovalWorkflowEnabled && displayCount > 0 && (
                        <StatusBadge
                          color="yellow"
                          text={t("{{count}} Awaiting Approval", {
                            count: displayCount,
                          })}
                        />
                      )}
                      {hasEditOrPublishPermission && (
                        <StatusLeft
                          color={getProductStatusColor(
                            product.status ?? "draft"
                          )}
                          text={getProductStatusText(
                            product.status ?? "draft",
                            t
                          )}
                          textStyle={{ fontSize: "13px" }}
                          productStatusType="product"
                        />
                      )}
                    </SubheaderContainer>
                    <SubheaderContainer>
                      <TemplatesNavIcon width={16} height={16} />
                      <InnerTitleText>{t("Template")}:</InnerTitleText>
                      <SubheaderContentText>
                        <LinkUnstyled
                          to={`${adminPath}/pim/templates/${product.product_schema.template_id}/groups`}
                        >
                          {product.product_schema.template_name}
                        </LinkUnstyled>
                      </SubheaderContentText>
                    </SubheaderContainer>
                    {product.pim_product_type === "variant" &&
                      product.master_product && (
                        <SubheaderContainer>
                          <MasterProductIcon width={16} height={16} />
                          <InnerTitleText>
                            {t("Master Product")}:
                          </InnerTitleText>
                          <SubheaderContentText>
                            {hasPermission("view_product_type_master") ? (
                              <LinkUnstyled
                                to={`${adminPath}/pim/products/${product.master_product.product_number}`}
                              >
                                {product.master_product.name}
                              </LinkUnstyled>
                            ) : (
                              <span>{product.master_product.name}</span>
                            )}
                          </SubheaderContentText>
                        </SubheaderContainer>
                      )}
                    <SubheaderContainer>
                      <TeamsIcon width={16} height={16} />
                      <InnerTitleText>{t("Team")}:</InnerTitleText>
                      <SubheaderContentText>{teamName}</SubheaderContentText>
                    </SubheaderContainer>

                    {(product.product_schema.show_total_completion_score ||
                      product.product_schema
                        .show_mandatory_completion_score) && (
                      <SubheaderContainer>
                        <CardCompletionBadgeIcon
                          width={16}
                          height={16}
                          stroke={theme.resolve("color_icon_secondary")}
                        />
                        <ScoreLabel>{t("Completion Score")}: </ScoreLabel>
                        {product.product_schema.show_total_completion_score && (
                          <CompletionScore
                            score={
                              product.display_status === "staged" ||
                              product.display_status === "unpublished_staged"
                                ? product.display_completion_score
                                : product.completion_score
                            }
                            type="total"
                            tooltipId="total-completion"
                            attributeCompletionValues={
                              product.attribute_completion_values
                            }
                          />
                        )}
                        {product.product_schema.show_total_completion_score &&
                          product.product_schema
                            .show_mandatory_completion_score && (
                            <span
                              style={{
                                lineHeight: "10px",
                                fontSize: fontSizeTokens.font_size_xs,
                              }}
                            >
                              |
                            </span>
                          )}
                        {product.product_schema
                          .show_mandatory_completion_score && (
                          <>
                            {(() => {
                              // Get the appropriate score based on display status
                              const score =
                                product.display_status === "staged" ||
                                product.display_status === "unpublished_staged"
                                  ? product.display_mandatory_completion_score ??
                                    product.mandatory_completion_score
                                  : product.mandatory_completion_score;

                              // Guard against invalid score
                              if (typeof score !== "number") return null;

                              return (
                                <CompletionScore
                                  score={score}
                                  type="mandatory"
                                  tooltipId="mandatory-completion"
                                  attributeCompletionValues={
                                    product.attribute_completion_values
                                  }
                                />
                              );
                            })()}
                          </>
                        )}
                      </SubheaderContainer>
                    )}
                    <SubheaderContainer>
                      <CalendarIcon width={16} height={16} />
                      <InnerTitleText>{t("Last Modified")}:</InnerTitleText>
                      <SubheaderContentText>
                        {moment(product.last_modified_full.modified_at).format(
                          "MMM DD, YYYY"
                        )}
                      </SubheaderContentText>
                    </SubheaderContainer>
                  </SubheaderInformationContainer>
                  <ProductHeaderActions>
                    <ProductShareButton
                      productId={productId}
                      slug={product.slug}
                      name={product.name}
                      produced_by={product.produced_by}
                      isLinkDisabled={isLinkDisabled}
                      isConnectionOn={isConnectionOn}
                      showLabel={true}
                    />{" "}
                    {showCreateSample && (
                      <SecondaryButtonSmallV2
                        onClick={() => setSampleVariant("seller")}
                      >
                        <LabFlaskIcon width={20} height={20} />
                        {t("Create Sample")}
                      </SecondaryButtonSmallV2>
                    )}
                    {showCreateQuote && (
                      <PrimaryButtonSmallV2
                        onClick={() => setQuoteVariant("seller")}
                      >
                        <CartOutlineIcon width={20} height={20} />
                        {t("Create Quote")}
                      </PrimaryButtonSmallV2>
                    )}
                    {statusActionButtons()}
                  </ProductHeaderActions>
                </HeaderContainer>
              </ProductHeader>
              <div className="product-details-section">
                <ProductGroupCard
                  product={product}
                  group={
                    product.product_schema.groups.find(
                      (group) => group.name.toLowerCase() === "general"
                    ) ?? product.product_schema.groups[0]
                  }
                  key={
                    product.product_schema.groups.find(
                      (group) => group.name.toLowerCase() === "general"
                    )?.id ?? product.product_schema.groups[0].id
                  }
                  mutateProduct={mutate}
                  isPortfolio={false}
                  replaceProductId={replaceProductId}
                  editInModal={true}
                  isChangeRequestEnabled={
                    storefront_metadata.enable_change_requests
                  }
                />
              </div>
            </ProductSidebarContent>
          </ProductSidebar>
        </ProductSidebarWrapper>
      )}

      <FullwidthPageWrapper style={{ width: "100%", flex: 1 }}>
        <ProductHeaderContainer
          ref={ref}
          pageLayout={pageLayout}
          isXlScreen={isXlScreen}
          productBackground={
            storefront_metadata.theme_object.designTokens?.productDetail
              ?.color_bg_surface_product?.value
          }
        >
          <Breadcrumb pageLayout={pageLayout} isXlScreen={isXlScreen}>
            <LinkUnstyled
              to={`${adminPath}/pim/products${
                sessionStorage.getItem("products_search") ?? ""
              }`}
            >
              {t("Products")}
            </LinkUnstyled>
            <div>/</div>
            <div>{product.name}</div>
          </Breadcrumb>
          {(pageLayout !== "two_column" ||
            (pageLayout === "two_column" && isLargeScreen)) && (
            <ProductHeader>
              {productCoverImage && (
                <ProductHeaderImage
                  src={productCoverImage}
                  alt={product.name}
                  onClick={() => setShowProductImageModal(true)}
                />
              )}
              <HeaderContainer>
                <div
                  style={{
                    display: "flex",
                    gap: theme.primitiveTokens.spacing.spacing_sm,
                    alignItems: "center",
                  }}
                >
                  <PageTitle>{product.name}</PageTitle>
                  <MasterVariantBadge
                    pim_product_type={product.pim_product_type ?? "standard"}
                    style={{ position: "static" }}
                  />
                </div>
                {isSmallScreen && (
                  <SubheaderContainer>
                    {isApprovalWorkflowEnabled && displayCount > 0 && (
                      <StatusBadge
                        color="yellow"
                        text={t("{{count}} Awaiting Approval", {
                          count: displayCount,
                        })}
                      />
                    )}
                    {hasEditOrPublishPermission && (
                      <StatusLeft
                        color={getProductStatusColor(product.status ?? "draft")}
                        text={getProductStatusText(
                          product.status ?? "draft",
                          t
                        )}
                        textStyle={{ fontSize: "13px" }}
                        productStatusType="product"
                      />
                    )}
                  </SubheaderContainer>
                )}
                <SubheaderInformationContainer>
                  <SubheaderContainer>
                    <TemplatesNavIcon width={16} height={16} />
                    <InnerTitleText>{t("Template")}:</InnerTitleText>
                    <SubheaderContentText>
                      <LinkUnstyled
                        to={`${adminPath}/pim/templates/${product.product_schema.template_id}/groups`}
                      >
                        {product.product_schema.template_name}
                      </LinkUnstyled>
                    </SubheaderContentText>
                  </SubheaderContainer>
                  {product.pim_product_type === "variant" &&
                    product.master_product && (
                      <SubheaderContainer>
                        <MasterProductIcon width={16} height={16} />
                        <InnerTitleText>{t("Master Product")}:</InnerTitleText>
                        <SubheaderContentText>
                          {hasPermission("view_product_type_master") ? (
                            <LinkUnstyled
                              to={`${adminPath}/pim/products/${product.master_product.product_number}`}
                            >
                              {product.master_product.name}
                            </LinkUnstyled>
                          ) : (
                            <span>{product.master_product.name}</span>
                          )}
                        </SubheaderContentText>
                      </SubheaderContainer>
                    )}
                  <SubheaderContainer>
                    <TeamsIcon width={16} height={16} />
                    <InnerTitleText>{t("Team")}:</InnerTitleText>
                    <SubheaderContentText>{teamName}</SubheaderContentText>
                  </SubheaderContainer>
                </SubheaderInformationContainer>

                <SubheaderInformationContainer>
                  {(product.product_schema.show_total_completion_score ||
                    product.product_schema.show_mandatory_completion_score) && (
                    <SubheaderContainer>
                      <CardCompletionBadgeIcon
                        width={16}
                        height={16}
                        stroke={theme.resolve("color_icon_secondary")}
                      />
                      <ScoreLabel>{t("Completion Score")}: </ScoreLabel>
                      {product.product_schema.show_total_completion_score && (
                        <CompletionScore
                          score={
                            product.display_status === "staged" ||
                            product.display_status === "unpublished_staged"
                              ? product.display_completion_score
                              : product.completion_score
                          }
                          type="total"
                          tooltipId="total-completion"
                          attributeCompletionValues={
                            product.attribute_completion_values
                          }
                        />
                      )}
                      {product.product_schema.show_total_completion_score &&
                        product.product_schema
                          .show_mandatory_completion_score && (
                          <span
                            style={{
                              lineHeight: "10px",
                              fontSize: fontSizeTokens.font_size_xs,
                            }}
                          >
                            |
                          </span>
                        )}
                      {product.product_schema
                        .show_mandatory_completion_score && (
                        <>
                          {(() => {
                            // Get the appropriate score based on display status
                            const score =
                              product.display_status === "staged" ||
                              product.display_status === "unpublished_staged"
                                ? product.display_mandatory_completion_score ??
                                  product.mandatory_completion_score
                                : product.mandatory_completion_score;

                            // Guard against invalid score
                            if (typeof score !== "number") return null;

                            return (
                              <CompletionScore
                                score={score}
                                type="mandatory"
                                tooltipId="mandatory-completion"
                                attributeCompletionValues={
                                  product.attribute_completion_values
                                }
                              />
                            );
                          })()}
                        </>
                      )}
                    </SubheaderContainer>
                  )}
                  <SubheaderContainer>
                    <CalendarIcon width={16} height={16} />
                    <InnerTitleText>{t("Last Modified")}:</InnerTitleText>
                    <SubheaderContentText>
                      {moment(product.last_modified_full.modified_at).format(
                        "MMM DD, YYYY"
                      )}
                    </SubheaderContentText>
                  </SubheaderContainer>
                </SubheaderInformationContainer>
              </HeaderContainer>

              <ProductHeaderActions>
                {!isSmallScreen && (
                  <>
                    {isApprovalWorkflowEnabled && displayCount > 0 && (
                      <StatusBadge
                        color="yellow"
                        text={t("{{count}} Awaiting Approval", {
                          count: displayCount,
                        })}
                      />
                    )}
                    {hasEditOrPublishPermission && (
                      <StatusLeft
                        color={getProductStatusColor(product.status ?? "draft")}
                        text={getProductStatusText(
                          product.status ?? "draft",
                          t
                        )}
                        textStyle={{ fontSize: "13px" }}
                        productStatusType="product"
                      />
                    )}
                  </>
                )}
                <ProductShareButton
                  productId={productId}
                  slug={product.slug}
                  name={product.name}
                  produced_by={product.produced_by}
                  isLinkDisabled={isLinkDisabled}
                  isConnectionOn={isConnectionOn}
                />
                {statusActionButtons()}
              </ProductHeaderActions>
            </ProductHeader>
          )}
        </ProductHeaderContainer>
        {product && (
          <ProfileDetailContentContainer
            pageContainerSettings={pageContainerSettings}
            pageLayout={pageLayout}
            surfaceBackground={
              storefront_metadata.theme_object.designTokens?.productDetail
                ?.color_bg_surface_product?.value
            }
            isXlScreen={isXlScreen}
            headerHeight={headerHeight}
          >
            <ProductTabs
              product={product}
              t={t}
              replaceProductId={replaceProductId}
              mutate={mutate}
              index={currentTab}
              onChange={handleTabChange}
              onRequestedTabChange={handleRequestedTabChange}
              tabsKey={product.id}
              requestedTabKey={query.tabKey || undefined}
              hasPermission={hasPermission}
              onVisibilityDataFetched={handleVisibilityDataFetched}
              isTabDisabled={isTabDisabled}
              isShowTableWithBulkAction={true}
              isShowGridAssetWithBulkAction={true}
              documentType={query.documentType || undefined}
              show_pricing_tab={show_pricing_tab}
              isApprovalWorkflowEnabled={isApprovalWorkflowEnabled}
              isApprovalWorkflowLoading={isApprovalWorkflowLoading}
              isDocumentAvailabilityLoading={isDocumentAvailabilityLoading}
              isListPricesLoading={isListPricesLoading}
              isRelatedEntitiesLoading={isRelatedEntitiesLoading}
              relatedEntities={relatedEntities}
              onDocumentTypeTabChange={handleDocumentTypeTabChange}
              onApprovalsCountChange={setInProgressApprovalsCount}
              hasAssetsOrExternalLinks={hasAssetsOrExternalLinks}
            />
          </ProfileDetailContentContainer>
        )}

        {showProductImageModal && productCoverImage && (
          <Modal
            show={showProductImageModal}
            closeModal={() => setShowProductImageModal(false)}
          >
            <ModalWrapper>
              <img
                src={productCoverImage}
                alt={product.name}
                style={{
                  maxWidth: fullSizeProductImage ? "auto" : "100%",
                  maxHeight: fullSizeProductImage ? "auto" : "100%",
                  cursor: fullSizeProductImage ? "zoom-out" : "zoom-in",
                }}
                onClick={() => setFullSizeProductImage((current) => !current)}
              />
            </ModalWrapper>
          </Modal>
        )}
      </FullwidthPageWrapper>

      {/* Seller Quote Modal */}
      <Modal
        show={quoteVariant === "seller"}
        closeModal={() => setQuoteVariant(null)}
        modalWidth={"95%"}
        overlay={true}
      >
        {quoteVariant === "seller" && (
          <SellerCreateQuote
            onSuccess={() => {
              setQuoteVariant(null);
            }}
          />
        )}
      </Modal>

      {/* Seller Sample Modal */}
      <Modal
        show={sampleVariant === "seller"}
        closeModal={() => setSampleVariant(null)}
        modalWidth={"95%"}
        overlay={true}
      >
        {sampleVariant === "seller" && (
          <SellerCreateSampleRequest
            onSuccess={(_id: any) => {
              setSampleVariant(null);
            }}
          />
        )}
      </Modal>
      {showArchiveWarningDialog && (
        <ObjectReferenceChangeConfirmDialog
          show={showArchiveWarningDialog}
          onClose={() => {
            setShowArchiveWarningDialog(false);
            setPendingArchiveAction(false);
            setLoadingState(undefined);
          }}
          onConfirm={async () => {
            setShowArchiveWarningDialog(false);
            await handleStatusUpdate("archived", {
              skipArchiveConfirmation: true,
            });
            setPendingArchiveAction(false);
          }}
          heading={t("Archive Product?")}
          message={getProductArchiveWarningMessage(t)}
        />
      )}
      <ConfirmDialog
        show={showArchiveBasicConfirmDialog}
        closeDialog={() => {
          setShowArchiveBasicConfirmDialog(false);
          setPendingArchiveAction(false);
          setLoadingState(undefined);
        }}
        confirmMessage={t(
          "This product will be archived. Are you sure you want to continue?"
        )}
        handleConfirm={() => {
          setShowArchiveBasicConfirmDialog(false);
          if (!pendingArchiveAction) {
            return;
          }
          setPendingArchiveAction(false);
          handleStatusUpdate("archived", {
            skipArchiveConfirmation: true,
          });
        }}
        useV2Buttons={true}
      />
    </div>
  );

  const productApprovalId = product.product_number || product.id;

  return isApprovalWorkflowEnabled ? (
    <ApprovalEligibilityProvider productId={productApprovalId}>
      {pageContent}
    </ApprovalEligibilityProvider>
  ) : (
    pageContent
  );
};
